/* ============================================================
   BNE STUDIO — ImageSlot
   Static recreation of the design handoff's <image-slot> web
   component (image-slot.js). In the design tool a user drops an
   image here; in this port it renders the component's empty
   state (faint frame + dashed ring + icon/caption), 1:1 with how
   the unfilled slots render in the original. Pass `src` to fill
   it with a real image later. Sizing comes from `style`/parent
   (slots are placed with position:absolute;inset:0 over their
   container's gradient), exactly like the original.
   ============================================================ */
import React from "react";

function shapeRadius(shape, radius) {
  switch (shape) {
    case "rect":
      return 0;
    case "circle":
      return "50%";
    case "pill":
      return 999;
    case "rounded":
    default:
      return typeof radius === "number" ? radius : parseInt(radius, 10) || 12;
  }
}

export default function ImageSlot({
  id,
  shape = "rounded",
  radius = 12,
  mask,
  fit = "cover",
  position = "50% 50%",
  placeholder = "Drop an image",
  src,
  className,
  style,
}) {
  const clip = mask ? { clipPath: mask, borderRadius: 0 } : { borderRadius: shapeRadius(shape, radius) };

  // Size comes from the consumer's `style`/`className`/parent (like the
  // original, where `.f` or inset:0 win over the component's :host default) —
  // so we deliberately don't hard-code width/height here.
  const host = {
    display: "inline-block",
    position: "relative",
    verticalAlign: "top",
    font: "13px/1.3 system-ui,-apple-system,sans-serif",
    color: "rgba(0,0,0,.55)",
    ...style,
  };

  return (
    <div id={id} className={className} style={host}>
      <div style={{ position: "absolute", inset: 0, overflow: "hidden", background: "rgba(0,0,0,.04)", ...clip }}>
        {src ? (
          <img
            alt=""
            draggable="false"
            src={src}
            style={{ width: "100%", height: "100%", objectFit: fit, objectPosition: position, display: "block" }}
          />
        ) : (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              textAlign: "center",
              padding: 12,
              boxSizing: "border-box",
            }}
          >
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ opacity: 0.45 }}
            >
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <path d="m21 15-5-5L5 21" />
            </svg>
            <div style={{ maxWidth: "90%", fontWeight: 500, letterSpacing: ".01em" }}>{placeholder}</div>
          </div>
        )}
        <div style={{ position: "absolute", inset: 0, pointerEvents: "none", border: "1.5px dashed rgba(0,0,0,.25)", ...clip }} />
      </div>
    </div>
  );
}
